import React, { useState } from 'react';
import { AreaOfStudy } from '../data/areaOfStudyData';
import { jobTitleDetails, JobTitleInfo } from '../data/jobTitleDetails';
import { BriefcaseIcon, TrendingUpIcon, DollarSignIcon, CheckCircleIcon, ExternalLinkIcon, ChevronDownIcon, GraduationCapIcon } from 'lucide-react';
interface ContentSectionProps {
  areaData: AreaOfStudy;
  selectedAreaId: number;
  onNavigate?: (page: string, slug?: string) => void;
}
function JobTitleCard({
  job,
  fallbackTitle
}: {
  job?: JobTitleInfo;
  fallbackTitle: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  if (!job) {
    return <div className="flex items-center gap-2 bg-gray-50 p-3 rounded-lg border border-gray-100" data-id="element-146" data-mp="71uok6h5ib4h">
        <div className="w-2 h-2 rounded-full bg-[#3b82f6]" data-id="element-147" data-mp="31tyfokowbhl"></div>
        <span className="font-medium" data-id="element-148" data-mp="2z7tzxt3ic3l">{fallbackTitle}</span>
      </div>;
  }
  return <div className="bg-gray-50 rounded-lg border border-gray-100 overflow-hidden" data-id="element-149" data-mp="3u6kcqh9mmxlm">
      <button type="button" onClick={() => setIsOpen(!isOpen)} className="w-full flex items-center justify-between p-3 text-left hover:bg-gray-100/50 transition-colors" data-id="element-150" data-mp="3p8r65n4ew1kt">
        <div className="flex items-center gap-2" data-id="element-151" data-mp="3mbkdlq20tsqx">
          <div className="w-2 h-2 rounded-full bg-[#3b82f6] flex-shrink-0" data-id="element-152" data-mp="3mdf1nd8la0z8"></div>
          <span className="font-medium text-gray-900" data-id="element-153" data-mp="38lgw1ce36n7t">{job.title}</span>
        </div>
        <ChevronDownIcon className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} data-id="element-154" data-mp="3djsak0fbtlwd" />
      </button>

      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-155" data-mp="94iouii3j1ya">
        <div className="px-3 pb-4 pt-1 border-t border-gray-200" data-id="element-156" data-mp="3r9m06kynoa75">
          <p className="text-sm text-gray-600 leading-relaxed mb-3" data-id="element-157" data-mp="3kruk62rup7">
            {job.description}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-id="element-158" data-mp="2fu71ncqsta9x">
            <div className="bg-white rounded-md p-2.5 border border-gray-100" data-id="element-159" data-mp="2kubg6x85buxi">
              <div className="flex items-center gap-1.5 mb-1" data-id="element-160" data-mp="2krys02bsooc0">
                <DollarSignIcon className="w-3.5 h-3.5 text-green-600" data-id="element-161" data-mp="20qx9gu9rq87j" />
                <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-162" data-mp="1wq3n45bz51gr">
                  Median Salary
                </span>
              </div>
              <span className="text-sm font-bold text-gray-900" data-id="element-163" data-mp="1n0tpyb29wsls">
                {job.medianSalary}
              </span>
            </div>

            <div className="bg-white rounded-md p-2.5 border border-gray-100" data-id="element-164" data-mp="1i2s7nsco8xbd">
              <div className="flex items-center gap-1.5 mb-1" data-id="element-165" data-mp="1d24izdbx0skb">
                <TrendingUpIcon className="w-3.5 h-3.5 text-blue-600" data-id="element-166" data-mp="1d4z4l86o0jfb" />
                <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-167" data-mp="191kmmbz61sz8">
                  Job Growth
                </span>
              </div>
              <span className="text-xs font-semibold text-gray-900" data-id="element-168" data-mp="33fbvvadl2au5">
                {job.growthRate}
              </span>
            </div>

            <div className="bg-white rounded-md p-2.5 border border-gray-100" data-id="element-169" data-mp="2ygqpsubngwxs">
              <div className="flex items-center gap-1.5 mb-1" data-id="element-170" data-mp="35yovsoumhpw9">
                <GraduationCapIcon className="w-3.5 h-3.5 text-purple-600" data-id="element-171" data-mp="3ay1jfbtbj0zw" />
                <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-172" data-mp="3fpb8obayg2b9">
                  Education
                </span>
              </div>
              <span className="text-xs font-semibold text-gray-900" data-id="element-173" data-mp="3knl3gksrp0wc">
                {job.education}
              </span>
            </div>
          </div>

          <p className="text-[11px] text-gray-400 mt-3 flex items-center gap-1" data-id="element-174" data-mp="2rz786ct2cioh">
            <ExternalLinkIcon className="w-3 h-3 flex-shrink-0" data-id="element-175" data-mp="2wz1vk6dmqq4s" />
            <span data-id="element-176" data-mp="2wxii0njcyt6p">
              Source:{' '}
              <a href="https://www.bls.gov/ooh/" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-600" data-id="element-177" data-mp="310wy1e7ar9k5">
                U.S. Bureau of Labor Statistics
              </a>
              {', '}
              <a href="https://www.bls.gov/oes/current/oes_nat.htm" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-600" data-id="element-178" data-mp="g94r8blvrkk3">
                Occupational Employment &amp; Wage Statistics
              </a>
            </span>
          </p>
        </div>
      </div>
    </div>;
}
export function ContentSection({
  areaData,
  selectedAreaId,
  onNavigate
}: ContentSectionProps) {
  const jobDetails = jobTitleDetails[areaData.id] || [];
  return <section className="w-full bg-white border-t border-gray-200 py-16" data-id="element-179" data-mp="xrlgayolfpzf">
      <div className="max-w-4xl mx-auto px-4" data-id="element-180" data-mp="xpsb7ntmpdtj">
        <h2 className="text-3xl font-bold text-gray-900 mb-8" data-id="element-181" data-mp="stjx8dr63qny">
          Is a Degree in {areaData.name} Right for You?
        </h2>

        <div className="prose prose-lg max-w-none text-gray-700" data-id="element-182" data-mp="17gwn2eo0cik5">
          <p className="text-xl leading-relaxed mb-10 text-gray-600" data-id="element-183" data-mp="12ibo0ikhs4xa">
            {areaData.whyStudy}
          </p>

          <div className="mb-12" data-id="element-184" data-mp="1gj5xl4ja0bxj">
            <div className="bg-blue-50 rounded-xl p-6 border border-blue-100" data-id="element-185" data-mp="1gg065in6cxyn">
              <div className="flex items-center gap-3 mb-4" data-id="element-186" data-mp="1cilyhvf56y8r">
                <div className="bg-blue-100 p-2 rounded-lg text-blue-600" data-id="element-187" data-mp="1v4tiirxazkhg">
                  <TrendingUpIcon className="w-6 h-6" data-id="element-188" data-mp="203wapbs6pw80" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 m-0" data-id="element-189" data-mp="201sbh758dle2">
                  Career Outlook
                </h3>
              </div>
              <p className="text-sm m-0" data-id="element-190" data-mp="1r2opnveoirvv">{areaData.careerOutlook}</p>
              <p className="text-[11px] text-blue-600 mt-3 m-0 flex items-center gap-1" data-id="element-191" data-mp="1cek7qtngake5">
                <ExternalLinkIcon className="w-3 h-3" data-id="element-192" data-mp="1ccqwf11w7bzt" />
                <a href="https://www.bls.gov/ooh/" target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-800" data-id="element-193" data-mp="1h628bglkaiyl">
                  Source: U.S. Bureau of Labor Statistics, Occupational Outlook
                  Handbook
                </a>
              </p>
            </div>
          </div>

          <div className="mb-12" data-id="element-194" data-mp="tg7v2xxbysdt">
            <div className="flex items-center gap-3 mb-2" data-id="element-195" data-mp="yesvoyghve2s">
              <BriefcaseIcon className="w-6 h-6 text-gray-400" data-id="element-196" data-mp="ye9fbm6hsxqv" />
              <h3 className="text-2xl font-bold text-gray-900 m-0" data-id="element-197" data-mp="30487ily2taw2">
                Common Job Titles
              </h3>
            </div>
            <p className="text-sm text-gray-500 mb-5 pl-9" data-id="element-198" data-mp="2vborlibx23o3">
              Click any job title to see salary data, growth outlook, and
              education requirements.
            </p>

            <div className="grid grid-cols-1 gap-3" data-id="element-199" data-mp="2mdv2osghecay">
              {areaData.jobTitles.map((title, idx) => {
              const jobInfo = jobDetails.find(j => j.title === title);
              return <JobTitleCard key={idx} job={jobInfo} fallbackTitle={title} data-id="element-200" data-mp="3f2a9wstdfqbq" data-mpi={idx} />;
            })}
            </div>

            <p className="text-[11px] text-gray-400 mt-3 pl-1 flex items-center gap-1" data-id="element-201" data-mp="3a0kznfdz3gop">
              <ExternalLinkIcon className="w-3 h-3" data-id="element-202" data-mp="3529pt4noh66h" />
              Salary and growth data sourced from the{' '}
              <a href="https://www.bls.gov/ooh/" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-600" data-id="element-203" data-mp="1yok5tm295223">
                Bureau of Labor Statistics
              </a>
            </p>
          </div>

          <div className="bg-gray-900 text-white rounded-2xl p-8 md:p-10 shadow-xl" data-id="element-204" data-mp="1ji4ndl0uws2e">
            <h3 className="text-2xl font-bold text-white mb-4 mt-0" data-id="element-205" data-mp="1ojbz1v64ebec">
              What to Look For in a Program
            </h3>
            <div className="flex items-start gap-4" data-id="element-206" data-mp="1y8vib1xqw79q">
              <CheckCircleIcon className="w-8 h-8 text-[#22c55e] flex-shrink-0 mt-1" data-id="element-207" data-mp="1yaev2ik7006r" />
              <p className="text-gray-300 text-lg leading-relaxed m-0" data-id="element-208" data-mp="229notnf8fy2b">
                {areaData.whatToLookFor}
              </p>
            </div>
          </div>

          {/* BLS Citation Footer */}
          <div className="mt-10 pt-6 border-t border-gray-200" data-id="element-209" data-mp="2c9y2f956j7i0">
            <p className="text-xs text-gray-400 leading-relaxed" data-id="element-210" data-mp="2h89dtob4hq2c">
              Employment projections and salary data are sourced from the{' '}
              <a href="https://www.bls.gov/ooh/" target="_blank" rel="noopener noreferrer" className="text-gray-500 underline hover:text-gray-700" data-id="element-211" data-mp="hw6ovyjqc3ha">
                U.S. Bureau of Labor Statistics (BLS) Occupational Outlook
                Handbook
              </a>{' '}
              and{' '}
              <a href="https://www.bls.gov/oes/" target="_blank" rel="noopener noreferrer" className="text-gray-500 underline hover:text-gray-700" data-id="element-212" data-mp="2jrmith70rvbb">
                Occupational Employment and Wage Statistics (OEWS)
              </a>
              . Data reflects national averages and may vary by location,
              experience, and employer. Projections cover the 2022–2032 decade.
              Last reviewed March 2026.
            </p>
          </div>
        </div>
      </div>
    </section>;
}